import { useState, useEffect } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { showSuccess, showError } from '../utils/sweetalert';
import useAuthStore from '../store/useAuthStore';
import axios from '../utils/axios';
import { sanitizeName, sanitizeEmail, sanitizePhone } from '../utils/security';

const Register = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectUrl = searchParams.get('redirect') || '/';
  const { register, loading } = useAuthStore();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
  });
  const [turnstileToken, setTurnstileToken] = useState('');

  useEffect(() => {
    let widgetId = null;
    const checkAndRender = () => {
      if (window.turnstile) {
        try {
          const container = document.getElementById('turnstile-container');
          if (container && container.innerHTML === '') {
            widgetId = window.turnstile.render('#turnstile-container', {
              sitekey: import.meta.env.VITE_CLOUDFLARE_TURNSTILE_SITE_KEY || '0x4AAAAAADoNV3lCYrASum1X',
              callback: (token) => {
                setTurnstileToken(token);
              },
              'expired-callback': () => {
                setTurnstileToken('');
              },
              'error-callback': () => {
                setTurnstileToken('');
              }
            });
          }
        } catch (e) {
          console.error("Turnstile rendering error:", e);
        }
      } else {
        setTimeout(checkAndRender, 100);
      }
    };
    
    checkAndRender();

    return () => {
      if (widgetId !== null && window.turnstile) {
        try {
          window.turnstile.remove(widgetId);
        } catch (e) {
          console.error("Turnstile remove error:", e);
        }
      }
    };
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // XSS Koruması - Input'ları temizle
    const sanitizedData = {
      firstName: sanitizeName(formData.firstName),
      lastName: sanitizeName(formData.lastName),
      email: sanitizeEmail(formData.email),
      phone: sanitizePhone(formData.phone),
      password: formData.password, // Şifre sanitize edilmez
    };

    // Validation
    if (!sanitizedData.firstName || !sanitizedData.lastName) {
      showError(t('auth.register.validation.names'));
      return;
    }

    if (!sanitizedData.email) {
      showError(t('auth.login.validation.email'));
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      showError(t('auth.register.validation.passwordMatch'));
      return;
    }

    if (formData.password.length < 6) {
      showError(t('auth.login.validation.password'));
      return;
    }

    // Captcha doğrulaması
    if (!turnstileToken) {
      showError(t('auth.login.validation.captcha'));
      return;
    }

    setIsSubmitting(true);
    try {
      // Captcha doğrula
      const captchaResult = await axios.post('/captcha/verify', {
        token: turnstileToken
      });

      if (!captchaResult.data.success) {
        showError(t('auth.captcha.verifyError'));
        if (window.turnstile) {
          window.turnstile.reset();
        }
        setTurnstileToken('');
        return;
      }

      // Captcha başarılı, kayıt ol
      await register(sanitizedData);
      showSuccess(
        t('auth.register.success.message'),
        t('auth.register.success.title')
      );
      navigate(redirectUrl);
    } catch (error) {
      showError(error.response?.data?.message || error.message || 'Kayıt başarısız!');
      if (window.turnstile) {
        window.turnstile.reset();
      }
      setTurnstileToken('');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center py-16 px-6 bg-champagne-50">
      <div className="max-w-2xl w-full">
        <div className="text-center mb-12">
          <h2 className="font-serif text-4xl font-light tracking-wide text-gray-900 mb-4">
            {t('auth.register.title')}
          </h2>
          <p className="text-gray-600 font-light tracking-wide">
            {t('auth.register.subtitle')}{' '}
            <Link to={`/login${redirectUrl !== '/' ? `?redirect=${encodeURIComponent(redirectUrl)}` : ''}`} className="text-gray-900 hover:underline font-light">
              {t('auth.register.loginLink')}
            </Link>
          </p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white p-8 md:p-12 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="firstName" className="block text-sm tracking-wider uppercase font-light text-gray-900 mb-2">
                {t('auth.register.firstName')}
              </label>
              <input
                id="firstName"
                name="firstName"
                type="text"
                required
                value={formData.firstName}
                onChange={handleChange}
                className="input-field"
                maxLength="50"
              />
            </div>
            <div>
              <label htmlFor="lastName" className="block text-sm tracking-wider uppercase font-light text-gray-900 mb-2">
                {t('auth.register.lastName')}
              </label>
              <input
                id="lastName"
                name="lastName"
                type="text"
                required
                value={formData.lastName}
                onChange={handleChange}
                className="input-field"
                maxLength="50"
              />
            </div>
          </div>

          <div>
            <label htmlFor="email" className="block text-sm tracking-wider uppercase font-light text-gray-900 mb-2">
              {t('auth.login.emailLabel')}
            </label>
            <input
              id="email"
              name="email"
              type="email"
              required
              value={formData.email}
              onChange={handleChange}
              className="input-field"
              placeholder={t('footer.emailPlaceholder')}
              maxLength="100"
            />
          </div>

          <div>
            <label htmlFor="phone" className="block text-sm tracking-wider uppercase font-light text-gray-900 mb-2">
              {t('auth.register.phone')}
            </label>
            <input
              id="phone"
              name="phone"
              type="tel"
              value={formData.phone}
              onChange={handleChange}
              className="input-field"
              placeholder="0555 555 55 55"
              maxLength="20"
              pattern="[0-9+\-() ]*"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="password" className="block text-sm tracking-wider uppercase font-light text-gray-900 mb-2">
                {t('auth.login.passwordLabel')}
              </label>
              <input
                id="password"
                name="password"
                type="password"
                required
                value={formData.password}
                onChange={handleChange}
                className="input-field"
                placeholder={t('auth.login.passwordLabel')}
                minLength="6"
                maxLength="100"
              />
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm tracking-wider uppercase font-light text-gray-900 mb-2">
                {t('auth.register.confirmPassword')}
              </label>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                required
                value={formData.confirmPassword}
                onChange={handleChange}
                className="input-field"
                minLength="6"
                maxLength="100"
              />
            </div>
          </div>

          {/* Cloudflare Turnstile */}
          <div className="border-t border-gray-100 pt-6 flex flex-col items-center justify-center">
            <label className="block text-sm tracking-wider uppercase font-light text-gray-900 mb-3 text-center w-full">
              {t('auth.captcha.label')}
            </label>
            <div id="turnstile-container" className="min-h-[65px] flex items-center justify-center w-full"></div>
          </div>

          <div className="flex items-start gap-3 pt-4">
            <input
              id="terms"
              name="terms"
              type="checkbox"
              required
              className="mt-1"
            />
            <label htmlFor="terms" className="text-sm text-gray-600 font-light leading-relaxed">
              <Trans
                i18nKey="auth.register.terms.agreement"
                components={{
                  1: <Link to="/terms" className="text-gray-900 hover:underline" />,
                  2: <Link to="/privacy" className="text-gray-900 hover:underline" />
                }}
              />
            </label>
          </div>

          <button
            type="submit"
            disabled={loading || isSubmitting || !turnstileToken}
            className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {(loading || isSubmitting) ? t('auth.register.submitting') : t('auth.register.submit')}
          </button>

          <div className="text-center pt-4">
            <Link
              to="/"
              className="text-gray-600 hover:text-gray-900 transition text-sm font-light tracking-wide"
            >
              {t('auth.login.continueShopping')}
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Register;

